import { isTerm } from '@1delta/lender-registry'
import type { TermAdapter } from './index'
import type { AuctionWindow } from '../types'

/**
 * Term Finance — fixed-rate, fixed-maturity tri-party repo.
 *
 * Borrowing exists ONLY inside a scheduled sealed-bid auction round, and most
 * repos sit between rounds at any moment. `canLend` is deliberately decoupled
 * from `canBorrow`: lending still works between rounds by buying repo tokens
 * on the secondary book, so a closed round leaves the market lend-only rather
 * than inert.
 */
export const termAdapter: TermAdapter = {
  id: 'term',
  matches: isTerm,
  profileId: () => 'term.auction@v1',
  build: (input) => {
    const auction = input.fixedTerm?.auction as AuctionWindow | undefined
    return {
      supply: {
        exit: {
          mode: 'market-sale',
          settlement: 'sync',
          priceRisk: 'market-price',
          partialAllowed: true,
        },
        counterparty: { kind: 'auction', solvency: 'overcollateralized' },
        availability: auction
          ? { canOpen: auction.canLend === true, window: auction }
          : undefined,
        maturity: { atMaturity: 'stops-earning' },
      },
      borrow: {
        debtShape: 'static-face',
        exit: {
          // No rebate — on a debt that never accrues, early repayment is
          // strictly worse than holding to maturity.
          earlyRepay: 'free',
          atMaturityCost: 'face',
          lateBehaviour: 'liquidatable',
          partialAllowed: true,
        },
        liquidation: { trigger: 'price', seizure: 'proportional' },
        counterparty: { kind: 'auction', solvency: 'overcollateralized' },
        availability: auction
          ? {
              canOpen: auction.canBorrow === true,
              blockedBy: auction.canBorrow ? undefined : 'auction-closed',
              window: auction,
              minSize: auction.minBorrowAmount,
            }
          : undefined,
      },
      constraints: { positionModel: 'account' },
    }
  },
}
